<section class="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-10 py-8 sm:py-10 animate-fade-in relative z-10 pb-20 sm:pb-24 overflow-x-hidden">

  @if (loading()) {
    <div class="card p-6 sm:p-10 rounded-[20px] bg-[#0c0c0c]/80 border border-white/[0.05] text-center">
      <p class="text-text-muted text-[14px]">Loading page content...</p>
    </div>
  } @else if (cms(); as page) {

    <!-- Header -->
    <div class="mb-8 sm:mb-10">
      <span
        class="inline-block text-gold text-[10px] sm:text-xs uppercase tracking-widest font-extrabold bg-gold/10 px-3 py-1.5 rounded-full border border-gold/25">
        {{ page.badge }}
      </span>
      <h1 class="font-display font-black text-[28px] sm:text-[36px] md:text-[44px] text-white tracking-wide mt-3 sm:mt-4 mb-2 break-words leading-tight">
        {{ page.title }}
      </h1>
      <p class="text-[13px] sm:text-[14px] text-text-muted max-w-xl leading-relaxed break-words">{{ page.subtitle }}</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8">

      <!-- Contact form (first on mobile) -->
      <div class="lg:col-span-8 order-1 lg:order-2">
        <form (ngSubmit)="submit()"
          class="card p-5 sm:p-6 md:p-8 rounded-[16px] sm:rounded-[20px] bg-[#0c0c0c]/80 border border-gold/20 shadow-[0_8px_30px_rgba(0,0,0,0.5)]">

          <div class="grid grid-cols-1 gap-4 sm:gap-5 mb-5">
            <div>
              <label class="block text-[11px] sm:text-[12px] font-bold text-text-muted uppercase tracking-wider mb-2">{{ page.form.emailLabel }}</label>
              <input type="email" [(ngModel)]="email" name="email" required
                [placeholder]="page.form.emailPlaceholder"
                class="w-full min-w-0 bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder:text-text-dim focus:outline-none focus:border-gold/40 transition-colors" />
            </div>

            <div>
              <label class="block text-[11px] sm:text-[12px] font-bold text-text-muted uppercase tracking-wider mb-2">{{ page.form.subjectLabel }}</label>
              <input type="text" [(ngModel)]="subject" name="subject" required
                [placeholder]="page.form.subjectPlaceholder"
                class="w-full min-w-0 bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder:text-text-dim focus:outline-none focus:border-gold/40 transition-colors" />
            </div>

            <div>
              <label class="block text-[11px] sm:text-[12px] font-bold text-text-muted uppercase tracking-wider mb-2">{{ page.form.messageLabel }}</label>
              <textarea [(ngModel)]="message" name="message" required rows="5"
                [placeholder]="page.form.messagePlaceholder"
                class="w-full min-w-0 bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white text-[14px] placeholder:text-text-dim focus:outline-none focus:border-gold/40 transition-colors resize-y min-h-[120px] sm:min-h-[150px]"></textarea>
            </div>

            <div>
              <label class="block text-[11px] sm:text-[12px] font-bold text-text-muted uppercase tracking-wider mb-2">{{ page.form.attachmentLabel }}</label>
              <label
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 w-full min-w-0 bg-[#050505] border border-dashed border-white/15 rounded-xl px-4 py-3 cursor-pointer hover:border-gold/30 transition-colors">
                <span class="text-[12px] sm:text-[13px] text-text-muted break-all sm:truncate order-2 sm:order-1">
                  {{ attachmentName || page.form.attachmentPlaceholder }}
                </span>
                <span class="btn-outline px-4 py-2 sm:py-1.5 rounded-lg text-[11px] font-bold uppercase shrink-0 text-center order-1 sm:order-2 w-full sm:w-auto">Browse</span>
                <input type="file" class="hidden" accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx"
                  (change)="onFileChange($event)" />
              </label>
              <p class="text-[11px] text-text-dim mt-2.5 font-medium leading-relaxed">
                <span class="text-gold">Restricted:</span> Max file size is 10MB. Allowed formats: JPG, PNG, GIF, PDF, DOC, DOCX.
              </p>
            </div>
          </div>

          <label class="flex items-start gap-3 mb-5 sm:mb-6 cursor-pointer group">
            <input type="checkbox" [(ngModel)]="humanVerified" name="humanVerified"
              class="mt-0.5 sm:mt-1 w-4 h-4 shrink-0 rounded border-white/20 bg-[#050505] text-gold focus:ring-gold/30" />
            <span class="text-text-muted text-[12px] sm:text-[13px] leading-relaxed group-hover:text-white/80 transition-colors break-words min-w-0">
              {{ page.form.verificationText }}
            </span>
          </label>

          <button type="submit" [disabled]="isSubmitting()"
            class="btn-gold w-full sm:w-auto px-8 py-3.5 rounded-xl text-[11px] sm:text-[12px] font-bold tracking-widest uppercase disabled:opacity-50 disabled:cursor-not-allowed">
            {{ isSubmitting() ? page.form.submittingButton : page.form.submitButton }}
          </button>
        </form>
      </div>

      <!-- Info sidebar -->
      <div class="lg:col-span-4 flex flex-col gap-4 sm:gap-5 order-2 lg:order-1">
        <div class="card p-5 sm:p-6 rounded-[16px] bg-[#0c0c0c]/80 border border-white/[0.05] min-w-0">
          <div class="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold mb-4 shrink-0">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
            </svg>
          </div>
          <h3 class="font-display font-bold text-white text-[15px] sm:text-[16px] mb-2 break-words">{{ page.generalInquiries.title }}</h3>
          <p class="text-text-muted text-[12px] sm:text-[13px] leading-relaxed break-words">{{ page.generalInquiries.description }}</p>
        </div>

        <div class="card p-5 sm:p-6 rounded-[16px] bg-[#0c0c0c]/80 border border-gold/20 min-w-0">
          <div class="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-gold/40 bg-gold/10 flex items-center justify-center text-gold mb-4 shrink-0">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round"
                d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
            </svg>
          </div>
          <h3 class="font-display font-bold text-white text-[15px] sm:text-[16px] mb-2 break-words">{{ page.community.title }}</h3>
          <p class="text-text-muted text-[12px] sm:text-[13px] leading-relaxed mb-4 break-words">{{ page.community.description }}</p>
          <div class="flex flex-col gap-2">
            @if (state.siteSettings().telegramUrl) {
              <a [href]="state.siteSettings().telegramUrl" target="_blank" rel="noopener noreferrer"
                class="footer-link text-[13px]">Telegram</a>
            }
            @if (state.siteSettings().discordUrl) {
              <a [href]="state.siteSettings().discordUrl" target="_blank" rel="noopener noreferrer"
                class="footer-link text-[13px]">Discord</a>
            }
            @if (state.siteSettings().xUrl) {
              <a [href]="state.siteSettings().xUrl" target="_blank" rel="noopener noreferrer"
                class="footer-link text-[13px]">Twitter / X</a>
            }
            @if (state.siteSettings().youtubeUrl) {
              <a [href]="state.siteSettings().youtubeUrl" target="_blank" rel="noopener noreferrer"
                class="footer-link text-[13px]">YouTube</a>
            }
          </div>
        </div>

        <div class="card p-5 sm:p-6 rounded-[16px] bg-[#0c0c0c]/80 border border-white/[0.05] min-w-0">
          <h3 class="font-display font-bold text-white text-[13px] sm:text-[14px] mb-3 uppercase tracking-wider break-words">{{ page.attachmentGuidelines.title }}</h3>
          <ul class="text-text-muted text-[12px] sm:text-[12.5px] space-y-2 leading-relaxed">
            @for (item of page.attachmentGuidelines.items; track item) {
              <li class="break-words">{{ item }}</li>
            }
          </ul>
        </div>
      </div>
    </div>
  }

</section>
